// The Ingest docket: a sectioned queue — Urgent, Needs review, Recently
// committed — and a per-document review screen reached by opening a row
// (read-only for a committed row).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchQueue, retryJob } from '../components/ingestApi';
import IngestReview from '../components/IngestReview';
import SampleReview from '../components/SampleReview';
import { SAMPLE_REVIEW_DATA } from '../components/sampleReviewData';
import { getOpenedSamples, takePendingSample } from '../components/sampleReviewState';

const humanize = (s: string | undefined) => String(s || '').replace(/_/g, ' ');
const pct = (c: number | null | undefined) => (c == null ? null : `${Math.round(Number(c) * 100)}%`);

const extOf = (filename: string) => {
  const dot = String(filename || '').lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toUpperCase().slice(0, 4) : 'DOC';
};

const riskPillClass = (riskClass: string) => {
  if (riskClass === 'critical') return 'pill pill--risk';
  if (riskClass === 'elevated') return 'pill pill--warn';
  if (riskClass === 'low') return 'pill pill--ok';
  return 'pill';
};

const isFakeId = (id: string) =>
  typeof id === 'string'
  && id.startsWith('sample-')
  && Object.prototype.hasOwnProperty.call(SAMPLE_REVIEW_DATA, id.slice('sample-'.length));

const NEEDS_REVIEW = ['awaiting_review', 'blocked'];
const COMMITTED = ['committed', 'indexed_only'];
const UNREADABLE = ['failed'];

const failureReason = (job: any) =>
  job.error_detail?.detail || job.error_detail?.error || 'This document could not be processed.';

interface QueueRowProps {
  job: any;
  onOpen: (id: string, readOnly: boolean) => void;
  onRetry?: (id: string) => void;
}

function QueueRow({ job, onOpen, onRetry }: QueueRowProps) {
  const entityConf = pct(job.doc_type_confidence);
  const lowMatch = job.doc_type_confidence != null && Number(job.doc_type_confidence) < 0.92;
  const failed = job.status === 'failed';
  const sub = failed
    ? failureReason(job)
    : [humanize(job.doc_type) || 'unclassified', job.target_entity_type ? humanize(job.target_entity_type) : null]
      .filter(Boolean).join(' · ');
  const settled = job.status === 'committed' || job.status === 'indexed_only';
  const actionable = job.status === 'awaiting_review' || job.status === 'blocked';
  const openable = actionable || job.status === 'committed';

  return (
    <button
      className={`ingest-row${job.urgency_class && !settled ? ' urgent' : ''}${openable ? '' : ' flat'}`}
      onClick={openable ? () => onOpen(job.id, !actionable) : undefined}
      data-testid={`ingest-row-${job.id}`}
      {...(openable ? {} : { tabIndex: -1 })}
    >
      <span className="ingest-row-ext">{extOf(job.original_filename)}</span>
      <span className="ingest-row-meta">
        <span className="ingest-row-name">{job.original_filename}</span>
        <span className={`ingest-row-sub${failed ? ' warn' : ''}`}>{sub}</span>
      </span>
      {failed ? (
        <>
          <span className="pill pill--risk">unreadable</span>
          {onRetry ? (
            <span
              className="ingest-row-retry"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onRetry(job.id); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onRetry(job.id); }
              }}
            >
              Retry
            </span>
          ) : null}
        </>
      ) : settled ? (
        <>
          <span className="pill pill--ok">
            {job.status === 'indexed_only' ? 'indexed' : 'committed'}
          </span>
          {job.status === 'committed' ? <span className="ingest-row-view" aria-hidden="true">View →</span> : null}
        </>
      ) : (
        <>
          {job.status === 'blocked' ? <span className="pill pill--risk">blocked</span> : null}
          {entityConf ? (
            <span className={`ingest-row-conf${lowMatch ? ' warn' : ''}`}>match <b>{entityConf}</b></span>
          ) : null}
          {job.risk_class ? <span className={riskPillClass(job.risk_class)}>{job.risk_class}</span> : null}
          {openable ? <span className="ingest-row-arrow" aria-hidden="true">→</span> : null}
        </>
      )}
    </button>
  );
}

interface IngestQueuePageProps {
  active?: boolean;
}

interface OpenState {
  id: string;
  readOnly: boolean;
  fake: boolean;
}

export default function IngestQueuePage({ active = false }: IngestQueuePageProps) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<OpenState | null>(null);
  const [openedSampleKeys, setOpenedSampleKeys] = useState<string[]>([]);
  const [openSeq, setOpenSeq] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const q = await fetchQueue({ limit: 100 }).catch(() => ({ jobs: [] }));
      setJobs(q.jobs || []);
    } catch (err) {
      console.error('Failed to load ingest queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const openJob = useCallback((id: string, readOnly: boolean) => setOpen({ id, readOnly, fake: isFakeId(id) }), []);

  const syncSamples = useCallback(() => {
    setOpenedSampleKeys((prev) => {
      const next = getOpenedSamples();
      return prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next;
    });
    const pending = takePendingSample();
    if (pending) {
      if (!SAMPLE_REVIEW_DATA[pending] || SAMPLE_REVIEW_DATA[pending].job.status === 'indexed_only') {
        setOpen((cur) => (cur && cur.fake ? null : cur));
      } else {
        setOpenSeq((n) => n + 1);
        setOpen({ id: `sample-${pending}`, readOnly: false, fake: true });
      }
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      if (!cancelled) await reload();
      if (cancelled) return;
      syncSamples();
    })();
    return () => { cancelled = true; };
  }, [active, reload, syncSamples]);

  useEffect(() => {
    window.addEventListener('pb:sample-opened', syncSamples);
    return () => window.removeEventListener('pb:sample-opened', syncSamples);
  }, [syncSamples]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current!);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const fakeJobs = useMemo(
    () => openedSampleKeys.map((k) => SAMPLE_REVIEW_DATA[k]?.job).filter(Boolean),
    [openedSampleKeys]
  );
  const allJobs = [...jobs, ...fakeJobs];

  const urgent = allJobs.filter((j) => j.urgency_class && NEEDS_REVIEW.includes(j.status));
  const needs = allJobs.filter((j) => !j.urgency_class && NEEDS_REVIEW.includes(j.status));
  const committed = allJobs.filter((j) => COMMITTED.includes(j.status));
  const unreadable = allJobs.filter((j) => UNREADABLE.includes(j.status));

  const criticalCount = allJobs.filter((j) => NEEDS_REVIEW.includes(j.status) && j.risk_class === 'critical').length;
  const committedTodayCount = allJobs.filter((j) => {
    if (j.status !== 'committed' || !j.updated_at) return false;
    const d = new Date(j.updated_at);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  }).length;

  const onRetry = useCallback(async (id: string) => {
    try {
      await retryJob(id);
      showToast('Queued for another attempt.');
      await reload();
    } catch (err: any) {
      showToast(err?.message || 'Could not retry this document.');
    }
  }, [reload, showToast]);

  interface SectionProps {
    label: string;
    urgent?: boolean;
    count?: number;
    children: React.ReactNode;
  }

  const Section = ({ label, urgent: isUrgent, count, children }: SectionProps) => (
    <>
      <div className="ingest-sec">
        <span className={`ingest-sec-lab${isUrgent ? ' urgent' : ''}`}>{label}</span>
        <span className="ingest-sec-rule" />
        {count != null ? <span className="ingest-sec-count">{count}</span> : null}
      </div>
      {children}
    </>
  );

  return (
    <div id="page-ingest" className={`page ingest-queue-page bi-console${active ? ' active' : ''}`}>
      <div className="ingest-docket">
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Document intake · nothing is written until you approve it</p>
              <h1 className="pagehead__title">Document Ingest Queue</h1>
            </div>
          </div>
        </div>
        <p className="ingest-queue-subtitle">
          Uploaded documents are classified, extracted, and staged as proposed changes.
          Nothing is written until you approve it.
        </p>

        <div className="ing-tiles">
          <div className="ind">
            <div className="ind__head"><p className="ind__abbr">Awaiting review</p></div>
            <div className="ind__value"><p className="ind__v">{urgent.length + needs.length}</p></div>
          </div>
          <div className={`ind${criticalCount ? ' ind--breach' : ''}`}>
            <div className="ind__head"><p className="ind__abbr">Critical flags</p></div>
            <div className="ind__value"><p className="ind__v">{criticalCount}</p></div>
          </div>
          <div className="ind">
            <div className="ind__head"><p className="ind__abbr">Committed today</p></div>
            <div className="ind__value"><p className="ind__v">{committedTodayCount}</p></div>
          </div>
        </div>

        {open ? (
          open.fake ? (
            <SampleReview
              key={`${open.id}#${openSeq}`}
              sample={SAMPLE_REVIEW_DATA[open.id.slice('sample-'.length)]}
              onBack={() => setOpen(null)}
              onToast={showToast}
            />
          ) : (
            <IngestReview
              jobId={open.id}
              readOnly={open.readOnly}
              onBack={() => setOpen(null)}
              onToast={showToast}
              onChanged={reload}
            />
          )
        ) : loading ? (
          <div className="state"><p className="state__body">Loading queue…</p></div>
        ) : (
          <>
            {urgent.length > 0 && (
              <Section label="Urgent — action required" urgent count={urgent.length}>
                {urgent.map((j) => <QueueRow key={j.id} job={j} onOpen={openJob} />)}
              </Section>
            )}

            <Section label="Needs review" count={needs.length}>
              {needs.length > 0 ? (
                needs.map((j) => <QueueRow key={j.id} job={j} onOpen={openJob} />)
              ) : (
                <div className="state">
                  <p className="state__body">Nothing waiting. Use the Upload button in the navbar to add documents.</p>
                </div>
              )}
            </Section>

            {unreadable.length > 0 && (
              <Section label="Couldn't be read" count={unreadable.length}>
                {unreadable.map((j) => <QueueRow key={j.id} job={j} onOpen={openJob} onRetry={onRetry} />)}
              </Section>
            )}

            {committed.length > 0 && (
              <Section label="Recently committed" count={committed.length}>
                {committed.map((j) => <QueueRow key={j.id} job={j} onOpen={openJob} />)}
              </Section>
            )}
          </>
        )}
      </div>

      {toast && <div className="ingest-toast show" role="status">{toast}</div>}
    </div>
  );
}
